/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * System Health Dashboard Page
 * ============================================================================
 *
 * Real-time system health monitoring dashboard showing service status,
 * OS resources (CPU, RAM, disk), database connections, JVM metrics,
 * and slow requests.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  Database,
  Server,
  Cpu,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Mail,
  CreditCard,
  HardDrive,
  BookOpen,
} from 'lucide-react';
import { useSystemHealth, useSlowRequests } from '@/hooks/api/useSystemHealth';
import { useLedgerHealth } from '@/hooks/api/useJournalLedger';
import type { DiskStatus } from '@/services/superadmin/system-health.service';
import { formatDistanceToNow } from 'date-fns';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatMb(mb: number) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

function statusColor(pct: number, override?: string) {
  if (override) return override;
  if (pct >= 90) return 'var(--color-error)';
  if (pct >= 70) return 'var(--color-warning)';
  return 'var(--color-success)';
}

// ── Ring Gauge (compact donut) ───────────────────────────────────────────────

function RingGauge({ value, size = 64, stroke = 6, color }: {
  value: number;
  size?: number;
  stroke?: number;
  color?: string;
}) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (circumference * Math.min(value, 100)) / 100;
  const fill = statusColor(value, color);

  return (
    <svg width={size} height={size} className="sh-ring">
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="var(--color-bg-tertiary)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={fill}
        strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        className="sh-ring-fill"
      />
      <text
        x={size / 2} y={size / 2 + 1}
        textAnchor="middle" dominantBaseline="middle"
        fill="var(--color-text-primary)"
        fontSize={size > 56 ? 14 : 11} fontWeight="700"
        fontFamily="var(--font-heading)"
      >
        {value.toFixed(0)}%
      </text>
    </svg>
  );
}

// ── Inline progress bar ──────────────────────────────────────────────────────

function InlineBar({ value, color }: { value: number; color?: string }) {
  const fill = statusColor(value, color);
  return (
    <div className="sh-inline-bar">
      <div className="sh-inline-bar-fill" style={{ width: `${Math.min(value, 100)}%`, background: fill }} />
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function SystemHealthPage() {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [slowRequestPage, setSlowRequestPage] = useState(0);

  const { data: health, isLoading, error, refetch } = useSystemHealth();
  const { data: slowRequests } = useSlowRequests(slowRequestPage, 10);
  const { data: ledgerHealth } = useLedgerHealth();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'UP':    return <CheckCircle className="sh-sicon sh-sicon-up" />;
      case 'DOWN':  return <XCircle className="sh-sicon sh-sicon-down" />;
      default:      return <AlertTriangle className="sh-sicon sh-sicon-warn" />;
    }
  };

  const getServiceIcon = (key: string) => {
    const map: Record<string, JSX.Element> = {
      api: <Server size={16} />, database: <Database size={16} />,
      redis: <Cpu size={16} />, email: <Mail size={16} />, payment: <CreditCard size={16} />,
    };
    return map[key] || <Activity size={16} />;
  };

  if (isLoading) {
    return (
      <div className="sh-page">
        <div className="sh-center-state"><RefreshCw className="sh-spin" size={24} /><span>Loading...</span></div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sh-page">
        <div className="sh-center-state sh-err"><AlertTriangle size={24} /><span>Failed to load</span></div>
        <style>{pageStyles}</style>
      </div>
    );
  }

  const os = health?.os;
  const jvm = health?.jvm;
  const disks = health?.disks ?? [];

  return (
    <div className="sh-page">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="sh-header">
        <h1 className="sh-title">
          <Activity className="sh-title-icon" size={22} />
          {t('systemHealth.title')}
        </h1>
        <button className="sh-btn" onClick={() => refetch()}>
          <RefreshCw size={14} /> {t('common.refresh')}
        </button>
      </div>

      {/* ── Services + quick metrics row ──────────────────────────────── */}
      <div className="sh-section sh-anim-1">
        <h2 className="sh-stitle">{t('systemHealth.services.title')}</h2>
        <div className="sh-svc-strip">
          {health && Object.entries(health.services).map(([key, svc]) => (
            <div key={key} className={`sh-svc sh-svc--${svc.status.toLowerCase()}`}>
              <div className="sh-svc-ico">{getServiceIcon(key)}</div>
              <span className="sh-svc-name">{svc.name}</span>
              <span className={`sh-svc-badge sh-svc-badge--${svc.status.toLowerCase()}`}>
                {getStatusIcon(svc.status)}
                {t(`systemHealth.status.${svc.status.toLowerCase()}`)}
              </span>
            </div>
          ))}

          {/* Separator */}
          <div className="sh-svc-sep" />

          {/* Quick metric: Error Rate — clicks to Application Metrics */}
          {health && (
            <div
              className={`sh-svc sh-svc--metric sh-svc--link ${health.requestMetrics.errorRate > 5 ? 'sh-svc--danger' : ''}`}
              onClick={() => document.getElementById('app-metrics')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            >
              <div className="sh-svc-ico"><AlertTriangle size={16} /></div>
              <div className="sh-svc-metric-body">
                <span className="sh-svc-metric-val">{health.requestMetrics.errorRate.toFixed(1)}%</span>
                <span className="sh-svc-metric-lbl">Error Rate</span>
              </div>
            </div>
          )}

          {/* Quick metric: Avg Response — clicks to Application Metrics */}
          {health && (
            <div
              className={`sh-svc sh-svc--metric sh-svc--link ${health.requestMetrics.avgResponseMs > 2000 ? 'sh-svc--warn' : ''}`}
              onClick={() => document.getElementById('app-metrics')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            >
              <div className="sh-svc-ico"><Clock size={16} /></div>
              <div className="sh-svc-metric-body">
                <span className="sh-svc-metric-val">{health.requestMetrics.avgResponseMs}ms</span>
                <span className="sh-svc-metric-lbl">Avg Response</span>
              </div>
            </div>
          )}

          {/* Quick metric: Disk — clicks to Disk Usage */}
          {health && health.disks.length > 0 && (() => {
            const worst = health.disks.reduce((a, b) => a.usagePercent > b.usagePercent ? a : b);
            return (
              <div
                className={`sh-svc sh-svc--metric sh-svc--link ${worst.usagePercent > 90 ? 'sh-svc--danger' : worst.usagePercent > 75 ? 'sh-svc--warn' : ''}`}
                onClick={() => document.getElementById('disk-usage')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              >
                <div className="sh-svc-ico"><HardDrive size={16} /></div>
                <div className="sh-svc-metric-body">
                  <span className="sh-svc-metric-val">{worst.usagePercent}%</span>
                  <span className="sh-svc-metric-lbl">Disk</span>
                </div>
              </div>
            );
          })()}

          {/* Quick metric: Requests/min — clicks to Application Metrics */}
          {health && (
            <div
              className="sh-svc sh-svc--metric sh-svc--link"
              onClick={() => document.getElementById('app-metrics')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            >
              <div className="sh-svc-ico"><Activity size={16} /></div>
              <div className="sh-svc-metric-body">
                <span className="sh-svc-metric-val">{health.requestMetrics.requestCount1m}</span>
                <span className="sh-svc-metric-lbl">Req/min</span>
              </div>
            </div>
          )}

          {/* Quick metric: Ledger Balance — clicks to Balance Verification */}
          {ledgerHealth && (
            <div
              className={`sh-svc sh-svc--metric sh-svc--link ${!ledgerHealth.allBalanced ? 'sh-svc--danger' : ''}`}
              onClick={() => navigate('/superadmin/journal/verify')}
              title={ledgerHealth.allBalanced
                ? t('platform:journal.health.allBalanced')
                : t('platform:journal.health.imbalancedCount', { count: ledgerHealth.imbalancedOrgs })}
            >
              <div className="sh-svc-ico"><BookOpen size={16} /></div>
              <div className="sh-svc-metric-body">
                <span className="sh-svc-metric-val">
                  {ledgerHealth.allBalanced ? (
                    <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                  ) : (
                    <span style={{ color: 'var(--color-error)' }}>{ledgerHealth.imbalancedOrgs}</span>
                  )}
                </span>
                <span className="sh-svc-metric-lbl">{t('platform:journal.health.ledger')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── System Resources (CPU · RAM · JVM) ─────────────────────────── */}
      {os && jvm && (
        <div id="sys-resources" className="sh-section sh-anim-2">
          <h2 className="sh-stitle">
            <Cpu size={16} /> {t('systemHealth.os.title')}
          </h2>
          <div className="sh-res-grid">

            {/* CPU */}
            <div className="sh-res-card">
              <RingGauge value={os.cpuUsagePercent} size={72} stroke={7} />
              <div className="sh-res-body">
                <div className="sh-res-top">
                  <span className="sh-res-label">{t('systemHealth.os.cpu')}</span>
                  <span className="sh-res-pct" style={{ color: statusColor(os.cpuUsagePercent) }}>
                    {os.cpuUsagePercent.toFixed(1)}%
                  </span>
                </div>
                <InlineBar value={os.cpuUsagePercent} />
                <div className="sh-res-meta">
                  <span>{os.availableProcessors} {t('systemHealth.os.cpuCores')}</span>
                  {os.systemLoadAverage >= 0 && (
                    <span>{t('systemHealth.os.loadAverage')}: {os.systemLoadAverage.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* System RAM */}
            <div className="sh-res-card">
              <RingGauge value={os.memoryUsagePercent} size={72} stroke={7} />
              <div className="sh-res-body">
                <div className="sh-res-top">
                  <span className="sh-res-label">{t('systemHealth.os.memory')}</span>
                  <span className="sh-res-pct" style={{ color: statusColor(os.memoryUsagePercent) }}>
                    {os.memoryUsagePercent.toFixed(1)}%
                  </span>
                </div>
                <InlineBar value={os.memoryUsagePercent} />
                <div className="sh-res-meta">
                  <span>{formatMb(os.usedMemoryMb)} / {formatMb(os.totalMemoryMb)}</span>
                  <span>{formatMb(os.freeMemoryMb)} free</span>
                </div>
              </div>
            </div>

            {/* JVM Heap */}
            <div className="sh-res-card">
              <RingGauge value={jvm.heapUtilizationPercent} size={72} stroke={7} color="var(--color-info)" />
              <div className="sh-res-body">
                <div className="sh-res-top">
                  <span className="sh-res-label">{t('systemHealth.jvm.heap')}</span>
                  <span className="sh-res-pct" style={{ color: 'var(--color-info)' }}>
                    {jvm.heapUtilizationPercent.toFixed(1)}%
                  </span>
                </div>
                <InlineBar value={jvm.heapUtilizationPercent} color="var(--color-info)" />
                <div className="sh-res-meta">
                  <span>{jvm.heapUsedMb} / {jvm.heapMaxMb} MB</span>
                  <span>{jvm.threadCount} {t('systemHealth.jvm.threads')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Disk Usage ─────────────────────────────────────────────────── */}
      {disks.length > 0 && (
        <div id="disk-usage" className="sh-section sh-anim-3">
          <h2 className="sh-stitle">
            <HardDrive size={16} /> {t('systemHealth.disk.title')}
          </h2>
          <div className="sh-disk-table">
            <div className="sh-disk-thead">
              <span className="sh-disk-col sh-disk-col--name">{t('systemHealth.disk.mount')}</span>
              <span className="sh-disk-col sh-disk-col--bar">{t('systemHealth.disk.used')}</span>
              <span className="sh-disk-col sh-disk-col--num">{t('systemHealth.disk.total')}</span>
              <span className="sh-disk-col sh-disk-col--num">{t('systemHealth.disk.free')}</span>
              <span className="sh-disk-col sh-disk-col--pct">%</span>
            </div>
            {disks.map((d, i) => {
              const col = statusColor(d.usagePercent);
              return (
                <div key={i} className="sh-disk-row">
                  <span className="sh-disk-col sh-disk-col--name">
                    <HardDrive size={14} className="sh-disk-ico" />
                    <span className="sh-disk-mount">{d.mount}</span>
                    <span className="sh-disk-type">{d.type}</span>
                  </span>
                  <span className="sh-disk-col sh-disk-col--bar">
                    <div className="sh-disk-bar">
                      <div className="sh-disk-bar-fill" style={{ width: `${Math.min(d.usagePercent, 100)}%`, background: col }} />
                    </div>
                  </span>
                  <span className="sh-disk-col sh-disk-col--num">{d.totalGb} GB</span>
                  <span className="sh-disk-col sh-disk-col--num">{d.freeGb} GB</span>
                  <span className="sh-disk-col sh-disk-col--pct" style={{ color: col }}>{d.usagePercent}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Application Metrics (DB Pool · Requests · Uptime) ──────────── */}
      <div id="app-metrics" className="sh-section sh-anim-4">
        <h2 className="sh-stitle">
          <Activity size={16} /> Application Metrics
        </h2>
        <div className="sh-app-grid">

          {/* DB Pool */}
          {health && (
            <div className="sh-app-card">
              <div className="sh-app-hdr"><Database size={15} /> {t('systemHealth.databasePool.title')}</div>
              <div className="sh-app-big">
                {health.databasePool.activeConnections}
                <span className="sh-app-of">/ {health.databasePool.maxConnections}</span>
              </div>
              <InlineBar value={health.databasePool.utilizationPercent} />
              <div className="sh-app-sub">{t('systemHealth.databasePool.idle')}: {health.databasePool.idleConnections}</div>
            </div>
          )}

          {/* Request Metrics */}
          {health && (
            <div className="sh-app-card">
              <div className="sh-app-hdr"><Activity size={15} /> {t('systemHealth.requests.title')}</div>
              <div className="sh-app-rows">
                <div className="sh-app-row">
                  <span>{t('systemHealth.requests.count')}</span>
                  <span className="sh-app-val">{health.requestMetrics.requestCount1m}</span>
                </div>
                <div className="sh-app-row">
                  <span>{t('systemHealth.requests.errors')}</span>
                  <span className="sh-app-val" style={{ color: health.requestMetrics.errorCount1m > 0 ? 'var(--color-error)' : undefined }}>
                    {health.requestMetrics.errorCount1m}
                  </span>
                </div>
                <div className="sh-app-row">
                  <span>{t('systemHealth.requests.avgResponse')}</span>
                  <span className="sh-app-val">{health.requestMetrics.avgResponseMs}ms</span>
                </div>
              </div>
            </div>
          )}

          {/* Uptime */}
          {health && (
            <div className="sh-app-card sh-app-card--uptime">
              <div className="sh-app-hdr"><Clock size={15} /> {t('systemHealth.uptime.title')}</div>
              <div className="sh-uptime-big">{health.uptimeFormatted}</div>
              <div className="sh-app-sub">{t('systemHealth.uptime.running')}</div>
            </div>
          )}
        </div>
      </div>

      {/* ── Slow Requests ──────────────────────────────────────────────── */}
      <div className="sh-section sh-anim-5">
        <h2 className="sh-stitle">
          {t('systemHealth.slowRequests.title')}
          <span className="sh-badge-warn">&gt;5s</span>
        </h2>
        <div className="sh-tbl-wrap">
          <table className="sh-tbl">
            <thead>
              <tr>
                <th>{t('systemHealth.slowRequests.endpoint')}</th>
                <th>{t('systemHealth.slowRequests.method')}</th>
                <th>{t('systemHealth.slowRequests.duration')}</th>
                <th>{t('systemHealth.slowRequests.time')}</th>
                <th>{t('systemHealth.slowRequests.ip')}</th>
              </tr>
            </thead>
            <tbody>
              {slowRequests?.content.map((r) => (
                <tr key={r.id}>
                  <td className="sh-tbl-uri" title={r.requestUri}>{r.requestUri}</td>
                  <td><span className={`sh-method sh-method--${r.requestMethod.toLowerCase()}`}>{r.requestMethod}</span></td>
                  <td className="sh-tbl-mono"><span className="sh-tbl-dur">{r.durationMs}ms</span></td>
                  <td className="sh-tbl-muted">{formatDistanceToNow(new Date(r.timestamp), { addSuffix: true })}</td>
                  <td className="sh-tbl-mono sh-tbl-muted">{r.ipAddress || '-'}</td>
                </tr>
              ))}
              {(!slowRequests || slowRequests.content.length === 0) && (
                <tr><td colSpan={5} className="sh-tbl-empty">{t('systemHealth.slowRequests.empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {slowRequests && slowRequests.totalPages > 1 && (
          <div className="sh-pag">
            <button className="sh-btn" disabled={slowRequests.first} onClick={() => setSlowRequestPage(p => p - 1)}>Previous</button>
            <span className="sh-pag-info">{(slowRequests.number ?? slowRequestPage) + 1} / {slowRequests.totalPages}</span>
            <button className="sh-btn" disabled={slowRequests.last} onClick={() => setSlowRequestPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      <style>{pageStyles}</style>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const pageStyles = `
  /* Page */
  .sh-page {
    display: flex; flex-direction: column; gap: 1.5rem;
    padding: 1.5rem 2rem; width: 100%; box-sizing: border-box;
  }

  /* Header */
  .sh-header {
    display: flex; justify-content: space-between; align-items: center;
    animation: sh-in-down 0.35s cubic-bezier(0.22,1,0.36,1) both;
  }
  .sh-title {
    font: 700 1.4rem/1.2 var(--font-heading); color: var(--color-text-primary);
    display: flex; align-items: center; gap: 0.6rem; margin: 0;
  }
  .sh-title-icon { color: var(--color-brand-primary); }

  .sh-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 7px 14px; font-size: 13px; font-weight: 500;
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md);
    background: var(--color-bg-secondary); color: var(--color-text-primary);
    cursor: pointer; transition: all 0.15s;
  }
  .sh-btn:hover:not(:disabled) { background: var(--color-bg-tertiary); border-color: var(--color-border-strong); }
  .sh-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  /* Sections */
  .sh-section { }
  .sh-stitle {
    font: 600 0.82rem/1 var(--font-heading); color: var(--color-text-secondary);
    text-transform: uppercase; letter-spacing: 0.06em;
    margin: 0 0 0.65rem 0; display: flex; align-items: center; gap: 0.4rem;
  }
  .sh-badge-warn {
    font-size: 10px; padding: 1px 6px; border-radius: var(--radius-sm);
    background: var(--color-warning-light); color: var(--color-warning);
    font-weight: 600; text-transform: none; letter-spacing: 0;
  }

  /* Staggered entrance */
  .sh-anim-1 { animation: sh-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.04s both; }
  .sh-anim-2 { animation: sh-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.08s both; }
  .sh-anim-3 { animation: sh-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.12s both; }
  .sh-anim-4 { animation: sh-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.16s both; }
  .sh-anim-5 { animation: sh-in-up 0.4s cubic-bezier(0.22,1,0.36,1) 0.20s both; }

  /* ── Services strip (centered with separator + metrics) ───────── */
  .sh-svc-strip {
    display: flex; flex-wrap: wrap; gap: 0.5rem;
    justify-content: center; align-items: center;
  }
  .sh-svc {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.55rem 0.85rem; border-radius: var(--radius-md);
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
  }
  .sh-svc--up   { border-left: 3px solid var(--color-success); }
  .sh-svc--down { border-left: 3px solid var(--color-error); }
  .sh-svc--degraded { border-left: 3px solid var(--color-warning); }
  .sh-svc--metric { border-left: 3px solid var(--color-brand-primary); }
  .sh-svc--link { cursor: pointer; transition: transform 0.15s ease, box-shadow 0.15s ease; }
  .sh-svc--link:hover { transform: translateY(-1px); box-shadow: var(--shadow-md); }
  .sh-svc--danger { border-left-color: var(--color-error); }
  .sh-svc--danger .sh-svc-metric-val { color: var(--color-error); }
  .sh-svc--warn { border-left-color: var(--color-warning); }
  .sh-svc--warn .sh-svc-metric-val { color: var(--color-warning); }
  .sh-svc-ico {
    width: 30px; height: 30px; border-radius: var(--radius-sm);
    background: var(--color-bg-tertiary); display: flex; align-items: center; justify-content: center;
    color: var(--color-text-muted); flex-shrink: 0;
  }
  .sh-svc-name { font-size: 13px; font-weight: 600; color: var(--color-text-primary); }
  .sh-svc-badge {
    font-size: 11px; display: inline-flex; align-items: center; gap: 3px;
  }
  .sh-svc-badge--up   { color: var(--color-success); }
  .sh-svc-badge--down { color: var(--color-error); }
  .sh-svc-badge--degraded { color: var(--color-warning); }
  .sh-sicon { width: 13px; height: 13px; }

  /* Vertical separator */
  .sh-svc-sep {
    width: 1px; height: 32px;
    background: var(--color-border-default);
    margin: 0 0.35rem; flex-shrink: 0;
  }

  /* Quick metric cards */
  .sh-svc-metric-body {
    display: flex; flex-direction: column; line-height: 1.15;
  }
  .sh-svc-metric-val {
    font: 700 15px var(--font-heading); color: var(--color-text-primary);
  }
  .sh-svc-metric-lbl {
    font-size: 10.5px; color: var(--color-text-muted); font-weight: 500;
  }

  /* ── Resource cards (CPU · RAM · JVM) — HORIZONTAL layout ────────── */
  .sh-res-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem;
  }
  .sh-res-card {
    display: flex; align-items: flex-start; gap: 1rem;
    padding: 1rem 1.15rem; border-radius: var(--radius-lg);
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
  }
  .sh-ring { flex-shrink: 0; }
  .sh-ring-fill { transition: stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1); }
  .sh-res-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.35rem; }
  .sh-res-top { display: flex; justify-content: space-between; align-items: baseline; }
  .sh-res-label { font-size: 13px; font-weight: 600; color: var(--color-text-primary); }
  .sh-res-pct { font: 700 1.1rem var(--font-heading); }
  .sh-res-meta {
    display: flex; justify-content: space-between; gap: 0.5rem;
    font-size: 11.5px; color: var(--color-text-muted); margin-top: 0.15rem;
  }

  /* Inline progress bar */
  .sh-inline-bar {
    height: 5px; border-radius: 3px; background: var(--color-bg-tertiary); overflow: hidden;
  }
  .sh-inline-bar-fill {
    height: 100%; border-radius: 3px;
    transition: width 0.6s cubic-bezier(0.22,1,0.36,1);
  }

  /* ── Disk table ──────────────────────────────────────────────────── */
  .sh-disk-table {
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg); overflow: hidden;
  }
  .sh-disk-thead {
    display: flex; padding: 0.5rem 1rem; gap: 0.5rem;
    background: var(--color-bg-tertiary); border-bottom: 1px solid var(--color-border-subtle);
    font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }
  .sh-disk-row {
    display: flex; padding: 0.6rem 1rem; gap: 0.5rem; align-items: center;
    border-bottom: 1px solid var(--color-border-subtle);
    transition: background 0.1s;
  }
  .sh-disk-row:last-child { border-bottom: none; }
  .sh-disk-row:hover { background: var(--color-bg-tertiary); }

  .sh-disk-col--name { flex: 2; display: flex; align-items: center; gap: 0.4rem; min-width: 0; }
  .sh-disk-col--bar  { flex: 3; }
  .sh-disk-col--num  { flex: 1; text-align: right; font-size: 12.5px; color: var(--color-text-secondary); font-family: var(--font-heading); }
  .sh-disk-col--pct  { flex: 0 0 48px; text-align: right; font: 700 13px var(--font-heading); }

  .sh-disk-ico { color: var(--color-text-muted); flex-shrink: 0; }
  .sh-disk-mount {
    font-size: 13px; font-weight: 600; color: var(--color-text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .sh-disk-type {
    font-size: 9.5px; font-weight: 600; padding: 0 5px; border-radius: 3px;
    background: var(--color-bg-tertiary); color: var(--color-text-muted);
    text-transform: uppercase; flex-shrink: 0;
  }
  .sh-disk-bar {
    height: 5px; border-radius: 3px; background: var(--color-bg-tertiary); overflow: hidden;
  }
  .sh-disk-bar-fill {
    height: 100%; border-radius: 3px;
    transition: width 0.6s cubic-bezier(0.22,1,0.36,1);
  }

  /* ── Application metrics ─────────────────────────────────────────── */
  .sh-app-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem;
  }
  .sh-app-card {
    padding: 1rem 1.15rem; border-radius: var(--radius-lg);
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    display: flex; flex-direction: column; gap: 0.5rem;
  }
  .sh-app-hdr {
    display: flex; align-items: center; gap: 0.4rem;
    font-size: 12px; font-weight: 600; color: var(--color-text-secondary);
    text-transform: uppercase; letter-spacing: 0.03em;
  }
  .sh-app-big {
    font: 700 1.6rem var(--font-heading); color: var(--color-text-primary); line-height: 1;
  }
  .sh-app-of { font-size: 0.85rem; font-weight: 500; color: var(--color-text-muted); }
  .sh-app-sub { font-size: 11.5px; color: var(--color-text-muted); }

  .sh-app-rows { display: flex; flex-direction: column; }
  .sh-app-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0.4rem 0; border-bottom: 1px solid var(--color-border-subtle);
    font-size: 13px; color: var(--color-text-secondary);
  }
  .sh-app-row:last-child { border-bottom: none; }
  .sh-app-val { font: 600 14px var(--font-heading); color: var(--color-text-primary); }

  .sh-app-card--uptime { justify-content: center; }
  .sh-uptime-big {
    font: 700 1.8rem var(--font-heading); color: var(--color-brand-primary); line-height: 1;
  }

  /* ── Table ───────────────────────────────────────────────────────── */
  .sh-tbl-wrap {
    background: var(--color-bg-secondary); border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg); overflow: hidden;
  }
  .sh-tbl { width: 100%; border-collapse: collapse; }
  .sh-tbl th {
    text-align: left; padding: 0.6rem 0.85rem; font-size: 10.5px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted);
    background: var(--color-bg-tertiary); border-bottom: 1px solid var(--color-border-subtle);
  }
  .sh-tbl td {
    padding: 0.6rem 0.85rem; font-size: 13px; color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .sh-tbl tr:last-child td { border-bottom: none; }
  .sh-tbl tbody tr:hover { background: var(--color-bg-tertiary); }
  .sh-tbl-uri {
    max-width: 320px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    font-family: monospace; font-size: 12px;
  }
  .sh-tbl-mono { font-family: monospace; font-size: 12px; }
  .sh-tbl-muted { color: var(--color-text-muted); font-size: 12px; }
  .sh-tbl-dur { color: var(--color-warning); font-weight: 600; }
  .sh-tbl-empty { text-align: center; color: var(--color-text-muted); padding: 2rem 1rem !important; }

  .sh-method {
    display: inline-block; padding: 1px 7px; border-radius: var(--radius-sm);
    font-size: 10.5px; font-weight: 700;
  }
  .sh-method--get    { background: var(--color-info-light); color: var(--color-info); }
  .sh-method--post   { background: var(--color-success-light); color: var(--color-success); }
  .sh-method--put    { background: var(--color-warning-light); color: var(--color-warning); }
  .sh-method--delete { background: var(--color-error-light); color: var(--color-error); }

  /* Pagination */
  .sh-pag {
    display: flex; justify-content: center; align-items: center; gap: 0.75rem;
    margin-top: 0.75rem; padding: 0.5rem;
  }
  .sh-pag-info { font-size: 12px; color: var(--color-text-muted); }

  /* States */
  .sh-center-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.5rem; padding: 4rem; color: var(--color-text-secondary);
  }
  .sh-err { color: var(--color-error); }

  /* Animations */
  @keyframes sh-in-down {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes sh-in-up {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes sh-spin-kf {
    to { transform: rotate(360deg); }
  }
  .sh-spin { animation: sh-spin-kf 1s linear infinite; }

  @media (prefers-reduced-motion: reduce) {
    .sh-header, .sh-anim-1, .sh-anim-2, .sh-anim-3, .sh-anim-4, .sh-anim-5,
    .sh-ring-fill, .sh-inline-bar-fill, .sh-disk-bar-fill {
      animation: none !important; transition: none !important;
    }
  }

  @media (max-width: 900px) {
    .sh-res-grid, .sh-app-grid { grid-template-columns: 1fr; }
    .sh-disk-col--bar { flex: 2; }
  }
  @media (max-width: 640px) {
    .sh-page { padding: 1rem; }
    .sh-header { flex-direction: column; align-items: flex-start; gap: 0.75rem; }
    .sh-svc-strip { justify-content: flex-start; }
    .sh-svc-sep { width: 100%; height: 1px; margin: 0.25rem 0; }
    .sh-disk-thead { display: none; }
    .sh-disk-row { flex-wrap: wrap; }
    .sh-disk-col--bar { flex: 100%; order: 10; }
  }
`;

export default SystemHealthPage;
